import { FormlyFieldConfig } from '@ngx-formly/core';
import { AgreementsFormOptions } from './agreements-form-options.interface';
import { toFormlyOptions } from '@lmc-app/summary/details/details-form-helpers';
import { FormControl } from '@angular/forms';

export function getCalculatorReferralFields(
        customFieldClass: string,
        options: Partial<AgreementsFormOptions>
    ): FormlyFieldConfig[] {
    return [
        {  
            fieldGroupClassName: 'row', 
            fieldGroup: [                
                {
                    // Skip + Update side by side
                    fieldGroupClassName: 'd-flex justify-content-end gap-2',
                    fieldGroup: [
                        {
                            type: 'button',
                            props: {
                                text: 'Skip to Loan Calculator',
                                stroked: true,
                                onClick: () => {
                                    options?.onSkip?.();
                                }
                            },
                            // Only when there is nothing to update
                            expressions: {
                                hide: (field: FormlyFieldConfig) => !options?.onSkip || !!field.form?.root?.dirty,
                            },
                        },
                        {
                            type: 'button',
                            props: {
                                text: 'Save',
                                onClick: (field: FormlyFieldConfig) => {
                                    options?.onUpdate?.(field.model);
                                }
                            },
                        },
                    ],
                },
                {
                    type: 'section-header',
                    props: {
                        label: 'Primary Debt',
                    },
                },
                {
                    key: 'Agency.value',
                    type: 'searchable-select', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Agency',
                        appearance: 'outline', 
                        floatLabel: 'always',
                        valueProp: 'id',
                        labelProp: 'Account_Name',
                        options: options.agencies$,
                        searchPlaceholder: 'Search agency',
                        noEntriesFoundLabel: 'No matching agency found',
                    },
                },              
                {
                    key: 'Creditor.value',
                    type: 'searchable-select', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Creditor',
                        appearance: 'outline', 
                        floatLabel: 'always',
                        options: options.creditors$,
                        valueProp: 'display_value',
                        labelProp: 'display_value',
                        searchPlaceholder: 'Search creditors',
                        noEntriesFoundLabel: 'No matching creditors found',
                        allowNewLabel: true,
                    },
                },                 
                {
                    key: 'MainAccountNumber.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Account Number',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                },     
                {
                    key: 'MainAccountBalance.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Account Balance',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    },
                },           
                {
                    key: 'MainCreditorPortfolio.value',
                    type: 'select', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Creditor Portfolio',
                        appearance: 'outline', 
                        floatLabel: 'always',
                        options: [
                            { label: 'Purchase', value: 'Purchase' },
                            { label: 'Contingency', value: 'Contingency' },
                        ]
                    },
                }, 
                {
                    key: 'MainCustomSettlement.value',
                    type: 'select', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Custom Settlement',
                        appearance: 'outline', 
                        floatLabel: 'always',
                        options: [
                            { label: 'Yes', value: 'Yes' },
                            { label: 'No', value: 'No' },
                        ],
                    },
                },             
                {
                    key: 'MainCustomSettlementAmount.value',
                    type: 'input', // Automatically creates a text input
                    className: customFieldClass,
                    props: {
                        label: 'Custom Agency Amount',
                        appearance: 'outline', 
                        floatLabel: 'always',
                    }
                },
                // Additional Debt FormArray
                {
                    fieldGroup: [
                        {
                            key: 'originalData.Additional_Debt',
                            type: 'repeat-additional-debt',
                            props: {
                                label: 'Additional debts',
                                addText: 'Add Additional Debt',
                                addIcon: 'add',
                                emptyMessage: 'No additional debts added yet',
                            },
                            fieldArray: {
                                fieldGroupClassName: 'row panel-small',
                                fieldGroup: [
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Agency.id',
                                        type: 'searchable-select',
                                        props: {
                                            label: 'Agency',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                            options: options.agencies$,
                                            valueProp: 'id',
                                            labelProp: 'Account_Name',
                                            searchPlaceholder: 'Search agencies',
                                            noEntriesFoundLabel: 'No matching agencies found',
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'misc',
                                        type: 'searchable-select',
                                        props: {
                                            label: 'Creditor',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                            options: options.creditors$,
                                            valueProp: 'display_value',
                                            labelProp: 'display_value',
                                            searchPlaceholder: 'Search creditors',
                                            noEntriesFoundLabel: 'No matching creditors found',
                                            allowNewLabel: true,
                                            panelClass: 'panel-small-select',
                                            required: true,
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Account_Number',
                                        type: 'input',
                                        props: {
                                            label: 'Account Number',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Creditor_Portfolio',
                                        type: 'select',
                                        props: {
                                            label: 'Creditor Portfolio',
                                            options: toFormlyOptions(options.additionalDebtCreditorPortfolios$),
                                            panelClass: 'panel-small-select',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Custom_Settlement',
                                        type: 'select',
                                        props: {
                                            label: 'Custom Settlement',
                                            required: true,
                                            panelClass: 'panel-small-select',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                            options: [
                                                { value: '', label: '' },
                                                { value: 'Yes', label: 'Yes' },
                                                { value: 'No', label: 'No' },
                                            ],
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Account_Balance',
                                        type: 'currency',
                                        props: {
                                            label: 'Account Balance',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                        },
                                        expressions: {
                                            'props.required': (field: FormlyFieldConfig) => {
                                                return !!field.model?.Creditor_Portfolio;
                                            },
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Custom_Agency_Amount',
                                        type: 'currency',
                                        props: {
                                            label: 'Custom Agency Amount',
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                        },
                                        expressions: {
                                            'props.required': (field: FormlyFieldConfig) => {
                                                return field.model?.Custom_Settlement === 'Yes';
                                            },
                                            hide: (field: FormlyFieldConfig) => {
                                                return field.model?.Custom_Settlement !== 'Yes';
                                            },
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Legal_Fee',
                                        type: 'currency',
                                        props: {
                                            label: 'Legal Fee',
                                            readonly: true,
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                        },
                                        expressions: {
                                            hide: (field: FormlyFieldConfig) => {
                                                return field.model?.HasSaha !== 'Yes';
                                            },
                                        },
                                    },
                                    {
                                        className: 'col-sm-6 col-lg-4',
                                        key: 'Settlement_Amount',
                                        type: 'currency',
                                        props: {
                                            label: 'Settlement Amount',
                                            readonly: true,
                                            appearance: 'outline', 
                                            floatLabel: 'always',
                                        },
                                        expressions: {
                                            hide: (field: FormlyFieldConfig) => {
                                                return field.model?.HasSaha !== 'Yes';
                                            },
                                        },
                                    },
                                ],
                            },
                        },                                   
                    ]
                }
            ]
        }
    ]
}